import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Moon, Sun, ExternalLink, TrendingUp, 
  Tag, Sparkles, AlertCircle, Copy, Check, Loader2, Calculator, Package, Search, ShoppingCart, Camera
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// eBay Affiliate Campaign ID
const EBAY_CAMPAIGN_ID = '5339135784';

// Check if item is a trading card (graded or ungraded)
const isCardItem = (item, keywords = '') => {
  if (!item && !keywords) return false;
  
  // Check if graded
  if (item?.is_graded) return true;
  
  // Check category
  const cardCategories = ['trading_cards', 'cards', 'collectible_cards', 'sports_cards'];
  if (item?.category && cardCategories.some(cat => item.category.toLowerCase().includes(cat))) {
    return true;
  }
  
  // Check keywords in model/description
  const searchText = `${item?.model || ''} ${item?.brand || ''} ${keywords}`.toLowerCase();
  const cardKeywords = ['psa', 'bgs', 'cgc', 'sgc', 'graded', 'pokemon', 'yu-gi-oh', 'yugioh', 
    'magic', 'mtg', 'baseball card', 'football card', 'basketball card', 'hockey card', 
    'topps', 'panini', 'fleer', 'upper deck', 'prizm', 'select', 'optic'];
  
  return cardKeywords.some(kw => searchText.includes(kw));
};

// Build eBay affiliate search URL
// For cards (graded or ungraded): uses Best Match and All listings (not just BIN)
// For other items: uses Lowest Price + Shipping and Buy It Now
const buildEbaySearchUrl = (keywords, options = {}) => {
  const { condition = null, buyItNow = true, sortLowest = true, soldOnly = false, isGraded = false, item = null } = options;
  const encodedKeywords = encodeURIComponent(keywords);
  
  // Check if this is a card item
  const isCard = isGraded || isCardItem(item, keywords);
  
  let url = `https://www.ebay.com/sch/i.html?_nkw=${encodedKeywords}&_sacat=0&rt=nc&campid=${EBAY_CAMPAIGN_ID}`;
  
  // Apply condition filter based on user selection
  if (condition === 'New') {
    url += '&LH_ItemCondition=1000'; // New condition
  } else if (condition === 'Used') {
    url += '&LH_ItemCondition=3000'; // Used condition
  }
  // If condition is null or 'All', no condition filter is applied
  
  // For cards: show ALL listings (auctions + BIN)
  // For other items: only Buy It Now
  if (buyItNow && !soldOnly && !isCard) {
    url += '&LH_BIN=1'; // Buy It Now only for non-cards
  }
  
  if (soldOnly) {
    url += '&LH_Sold=1'; // Sold items only
    url += '&_sop=13'; // Sort by End Date: recent first
  } else if (!isCard && sortLowest) {
    url += '&_sop=15'; // Sort by Price + Shipping: lowest first (non-cards only)
  }
  // For cards: NO sort parameter = eBay default (Best Match)
  
  return url;
};

export default function ScanResultPage() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { token } = useAuth();
  
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(null); // Track current condition
  const [refreshing, setRefreshing] = useState(false);

  // Get the displayed price - average of 10 lowest listings (includes shipping)
  const getDisplayPrice = () => {
    if (!scan) return 0;
    return scan.average_price;
  };

  useEffect(() => {
    // First check sessionStorage for guest scan data
    const storedScan = sessionStorage.getItem(`search_${scanId}`) || sessionStorage.getItem(`scan_${scanId}`);
    if (storedScan) {
      try {
        const scanData = JSON.parse(storedScan);
        console.log('Using scan data from sessionStorage');
        setScan(scanData);
        setSelectedCondition(scanData.item?.condition || 'New');
        setLoading(false);
        return;
      } catch (e) {
        console.error('Failed to parse stored scan data:', e);
      }
    }
    
    // Check navigation state as fallback
    const stateData = location.state?.scanData;
    if (stateData) {
      console.log('Using scan data from navigation state');
      setScan(stateData);
      setSelectedCondition(stateData.item?.condition || 'New');
      setLoading(false);
      return;
    }
    
    // Otherwise fetch from API (for logged-in users with saved scans)
    fetchScan();
  }, [scanId, location.state]);

  const fetchScan = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/scans/${scanId}`, { headers });
      setScan(response.data);
      setSelectedCondition(response.data.item?.condition || 'New');
    } catch (error) {
      toast.error('Failed to load search result');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Re-search with different condition (updates existing scan, doesn't create new one)
  const handleConditionChange = async (newCondition) => {
    if (newCondition === selectedCondition || refreshing) return;
    
    setRefreshing(true);
    setSelectedCondition(newCondition);
    
    try {
      // Use refresh endpoint to update existing scan
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/scans/${scanId}/refresh`, {
        condition: newCondition
      }, { headers });
      
      // Update scan data in place
      setScan(response.data);
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Failed to refresh with new condition');
      setSelectedCondition(scan.item?.condition || 'New');
    } finally {
      setRefreshing(false);
    }
  };

  const copyToClipboard = () => {
    if (!scan) return;
    const marketValue = getDisplayPrice();
    const itemName = getItemDisplayName();
    const text = `${itemName} - Market Value: $${marketValue.toFixed(2)}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Get display name without duplicate brand
  const getItemDisplayName = () => {
    if (!scan) return '';
    if (scan.item.model?.toLowerCase().startsWith(scan.item.brand?.toLowerCase())) {
      return scan.item.model;
    }
    return `${scan.item.brand || ''} ${scan.item.model}`.trim();
  };

  // Build search keywords from item data (avoiding duplicate grading info)
  const getSearchKeywords = () => {
    if (!scan) return '';
    
    // Start with model (which may already contain grading info from the search)
    let searchText = scan.item.model || '';
    
    // Add brand if not already in model
    if (scan.item.brand && !searchText.toLowerCase().includes(scan.item.brand.toLowerCase())) {
      searchText = `${scan.item.brand} ${searchText}`.trim();
    }
    
    // Only add grading_info if it's NOT already in the search text
    if (scan.item.grading_info) {
      const gradingLower = scan.item.grading_info.toLowerCase();
      const searchLower = searchText.toLowerCase();
      
      // Check if grading info (e.g., "PSA 8") is already present
      if (!searchLower.includes(gradingLower)) {
        // Also check for variations like "psa8" vs "psa 8"
        const gradingNoSpace = gradingLower.replace(/\s+/g, '');
        const searchNoSpace = searchLower.replace(/\s+/g, '');
        if (!searchNoSpace.includes(gradingNoSpace)) {
          searchText = `${searchText} ${scan.item.grading_info}`.trim();
        }
      }
    }
    
    // Add color if not already in search text
    if (scan.item.color && !searchText.toLowerCase().includes(scan.item.color.toLowerCase())) {
      searchText = `${searchText} ${scan.item.color}`.trim();
    }
    
    return searchText;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Scan not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const confidencePercent = Math.round(scan.item.confidence * 100);
  const searchKeywords = getSearchKeywords();
  const hasGoodIdentification = confidencePercent >= 70 && searchKeywords.length > 5;

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              data-testid="copy-btn"
            >
              {copied ? <Check className="w-5 h-5 text-primary" /> : <Copy className="w-5 h-5" />}
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Item Identification */}
        <div className="mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
              {scan.comps?.[0]?.image_url ? (
                <img 
                  src={scan.comps[0].image_url} 
                  alt={scan.item.model}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Tag className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  {scan.item.category}
                </Badge>
                <Badge 
                  variant={scan.item.condition === 'New' ? 'default' : 'outline'}
                  className="text-xs"
                >
                  {scan.item.condition}
                </Badge>
                {scan.item.grading_info && (
                  <Badge variant="default" className="text-xs bg-amber-500 hover:bg-amber-600">
                    {scan.item.grading_info}
                  </Badge>
                )}
              </div>
              <h1 className="text-xl font-bold mb-1" data-testid="item-title">
                {/* Remove duplicate brand and grading info from model if present */}
                {(() => {
                  let displayName = scan.item.model || '';
                  // Remove duplicate brand
                  if (scan.item.brand && displayName.toLowerCase().startsWith(scan.item.brand.toLowerCase())) {
                    displayName = displayName;
                  } else if (scan.item.brand) {
                    displayName = `${scan.item.brand} ${displayName}`;
                  }
                  // Remove duplicate grading info if already in model name
                  if (scan.item.grading_info) {
                    const gradingParts = scan.item.grading_info.split(' ');
                    gradingParts.forEach(part => {
                      const regex = new RegExp(`\\b${part}\\b`, 'gi');
                      displayName = displayName.replace(regex, '');
                    });
                    displayName = displayName.replace(/\s+/g, ' ').trim();
                  }
                  return displayName;
                })()}
              </h1>
              {scan.item.color && (
                <p className="text-sm text-muted-foreground">{scan.item.color}</p>
              )}
            </div>
          </div>

          {/* Confidence indicator */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">AI Confidence</span>
                <span className="font-medium">{confidencePercent}%</span>
              </div>
              <Progress value={confidencePercent} className="h-1.5" />
            </div>
          </div>
        </div>

        {/* Price Card */}
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 mb-6" data-testid="price-card">
          {/* Condition Toggle - Above Estimated Value */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-primary">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-medium">Estimated Market Value</span>
            </div>
            
            <div className="flex items-center gap-1 bg-background/80 rounded-lg p-1">
              <button
                onClick={() => handleConditionChange('New')}
                disabled={refreshing}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  selectedCondition === 'New' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                New
              </button>
              <button
                onClick={() => handleConditionChange('Used')}
                disabled={refreshing}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  selectedCondition === 'Used' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Used
              </button>
            </div>
          </div>
          
          {refreshing ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Searching {selectedCondition}...</span>
            </div>
          ) : (
          <>
          {/* Condensed Price Display - Weighted average of 10 lowest listings */}
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-bold font-mono text-primary" data-testid="average-price">
              ${getDisplayPrice().toFixed(2)}
            </span>
          </div>
          
          <p className="text-xs text-muted-foreground mb-2">
            {scan.valuation_note || "Weighted avg of lowest active listings"}
          </p>
          
          <div className="flex items-center gap-3 text-xs mb-3">
            <span className="text-muted-foreground">Low: <span className="font-mono font-medium text-foreground">${(scan.lowest_price || scan.suggested_low).toFixed(2)}</span></span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">High: <span className="font-mono font-medium text-foreground">${scan.suggested_high.toFixed(2)}</span></span>
          </div>
          
          {/* Compact Action Buttons - eBay Links First */}
          <div className="flex gap-2 mb-2">
            <a
              href={buildEbaySearchUrl(searchKeywords, { condition: selectedCondition, buyItNow: true, sortLowest: true, isGraded: scan.item?.is_graded, item: scan.item })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button className="w-full gap-1.5 h-9 text-sm" variant="default">
                <Search className="w-3.5 h-3.5" />
                Search eBay
                <ExternalLink className="w-3 h-3 opacity-70" />
              </Button>
            </a>
            
            <a
              href={buildEbaySearchUrl(searchKeywords, { condition: selectedCondition, soldOnly: true, isGraded: scan.item?.is_graded, item: scan.item })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button className="w-full gap-1.5 h-9 text-sm" variant="outline">
                Sold Listings
                <ExternalLink className="w-3 h-3 opacity-70" />
              </Button>
            </a>
          </div>
          
          {/* Profit Calculator & Inventory Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => {
                navigate('/calculator', {
                  state: {
                    itemName: getItemDisplayName(),
                    estimatedValue: getDisplayPrice()
                  }
                });
                window.scrollTo(0, 0);
              }}
              variant="secondary"
              size="sm"
              className="flex-1 gap-1.5 h-9"
            >
              <Calculator className="w-3.5 h-3.5" />
              Profit Calculator
            </Button>
            <Button
              onClick={() => {
                navigate('/inventory', {
                  state: {
                    itemName: getItemDisplayName(),
                    estimatedValue: getDisplayPrice(),
                    photo: scan.comps?.[0]?.image_url,
                    condition: selectedCondition // Pass the condition from search
                  }
                });
                window.scrollTo(0, 0);
              }}
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5 h-9"
            >
              <Package className="w-3.5 h-3.5" />
              Inventory
            </Button>
          </div>
          </>
          )}
        </div>

        {/* Search Another Item - Below Quick Actions */}
        <div className="mb-4">
          <Button 
            onClick={() => {
              navigate('/dashboard');
              window.scrollTo(0, 0);
            }}
            className="w-full h-10 gap-2"
            variant="outline"
          >
            <Search className="w-4 h-4" />
            Search Another Item
          </Button>
        </div>

        {/* Key Features */}
        {scan.item.key_features?.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold mb-3">Key Features</h3>
            <div className="flex flex-wrap gap-2">
              {scan.item.key_features.map((feature, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {feature}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {scan.item.description && (
          <div className="mb-6">
            <h3 className="font-bold mb-2">Description</h3>
            <p className="text-sm text-muted-foreground">{scan.item.description}</p>
          </div>
        )}

        {/* Comparable Listings */}
        <div>
          {/* Header with Condition Toggle */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">
              Active Listings ({scan.comps?.length || 0})
            </h3>
            
            {/* Condition Toggle */}
            <div className="flex items-center gap-1 bg-secondary rounded-lg p-1">
              <button
                onClick={() => handleConditionChange('New')}
                disabled={refreshing}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  selectedCondition === 'New' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                New
              </button>
              <button
                onClick={() => handleConditionChange('Used')}
                disabled={refreshing}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  selectedCondition === 'Used' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Used
              </button>
            </div>
          </div>

          {refreshing ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Searching {selectedCondition} listings...</span>
            </div>
          ) : scan.comps?.length === 0 || (scan.lowest_price === 0 && scan.average_price === 0) ? (
            /* No Listings Found - Helpful Message */
            <div className="text-center py-8 px-4 bg-secondary/30 rounded-xl">
              <AlertCircle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
              <h4 className="font-semibold mb-2">No Exact Matches Found</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Try a different angle, include brand tags, or search manually on eBay
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <a
                  href={buildEbaySearchUrl(searchKeywords, { newOnly: false, buyItNow: false, sortLowest: true, isGraded: scan.item?.is_graded, item: scan.item })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="default" className="gap-2">
                    <Search className="w-4 h-4" />
                    Search eBay Manually
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="gap-2"
                >
                  <Search className="w-4 h-4" />
                  Try Different Search
                </Button>
              </div>
            </div>
          ) : (
          <div className="grid gap-3">
            {scan.comps?.map((comp, index) => (
              <a
                key={index}
                href={comp.listing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start sm:items-center gap-3 p-3 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-md transition-all comp-card"
                data-testid={`comp-${index}`}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                  <img 
                    src={comp.image_url} 
                    alt={comp.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100';
                    }}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2 sm:truncate">{comp.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">{comp.condition}</Badge>
                    <span className="font-bold font-mono text-base sm:hidden text-primary">${comp.price.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="hidden sm:flex flex-col items-end flex-shrink-0">
                  <p className="font-bold font-mono text-lg text-primary">${comp.price.toFixed(2)}</p>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
              </a>
            ))}
          </div>
          )}
          
          {/* View More on eBay */}
          {hasGoodIdentification && scan.comps?.length > 0 && (
            <a
              href={buildEbaySearchUrl(searchKeywords, { 
                newOnly: selectedCondition === 'New', 
                buyItNow: false,
                sortLowest: true,
                isGraded: scan.item?.is_graded,
                item: scan.item
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4"
            >
              <Button variant="ghost" className="w-full gap-2 text-muted-foreground hover:text-foreground">
                View more listings on eBay
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          )}
          
          <p className="text-xs text-muted-foreground text-center mt-4">
            * Prices include shipping, sorted by lowest total price
          </p>
        </div>

        {/* Search Another Button */}
        <div className="mt-8">
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full h-12 rounded-full font-bold"
            data-testid="search-another-btn"
          >
            Search Another Item
          </Button>
        </div>
      </main>
    </div>
  );
}
